import type { PaymentProvider, ProviderConfig } from '../types/common/payment'

/** Flat `GET .../integrations/provider/default/public` body (mode-specific `providerConfig`). */
export type PublicDefaultPaymentProviderApiResponse = {
  provider?: PaymentProvider['provider'] | string
  providerConfig?: ProviderConfig | Record<string, unknown> | null
  whiteLabelProvider?: string
  paymentSetting?: { defaultPaymentProvider?: string }
  defaultPaymentProvider?: Pick<PublicDefaultPaymentProviderApiResponse, 'provider' | 'providerConfig' | 'whiteLabelProvider'>
}

function coercePublicDefaultProviderBody(
  data: PublicDefaultPaymentProviderApiResponse | Record<string, unknown> | null | undefined
): PublicDefaultPaymentProviderApiResponse | null {
  if (!data || typeof data !== 'object') return null
  const d = data as PublicDefaultPaymentProviderApiResponse
  if (d.provider && d.providerConfig != null) return d
  const nested = d.defaultPaymentProvider
  if (nested && typeof nested === 'object' && !Array.isArray(nested) && nested.provider && nested.providerConfig != null) {
    return {
      provider: nested.provider,
      providerConfig: nested.providerConfig,
      whiteLabelProvider: nested.whiteLabelProvider,
      paymentSetting: d.paymentSetting,
    }
  }
  return null
}

/**
 * Maps the public default-provider payload into {@link PaymentProvider}.
 * `providerConfig` may be flat or `{ test, live }`; call
 * {@link isPublicDefaultPaymentProviderConfigured}(..., liveMode) for validation.
 */
export function paymentProviderFromPublicDefaultApi(
  data: PublicDefaultPaymentProviderApiResponse | Record<string, unknown> | null | undefined
): PaymentProvider | null {
  const body = coercePublicDefaultProviderBody(data)
  if (!body?.provider) return null

  const provider = body.provider as PaymentProvider['provider']
  if (provider === 'manual') {
    return {
      provider: 'manual',
      providerConfig: { test: {}, live: {} },
      title: '',
      imageUrl: '',
      description: '',
    }
  }

  const raw = body.providerConfig
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null

  return {
    provider,
    providerConfig: raw as unknown as PaymentProvider['providerConfig'],
    title: '',
    imageUrl: '',
    description: '',
    whiteLabelProvider: body.whiteLabelProvider,
  }
}
